import React, { useState } from "react";
import { useFormikContext } from "formik";
import cx from "classnames";
import AutomatedEmployerMatchingTeaser from "./EmployerMatchingTeaser";
import EmployerMatchingHeader from "./EmployerMatchingHeader";
import EmployerLookup from "./EmployerLookup";
import EmployerMatchingFooter from "./EmployerMatchingFooter";

/**
 * @param {object} props
 * @param {string} props.spinnerColor
 * @param {string} props.employerField
 * @param {string} props.sectionHeading
 * @param {string} props.description
 * @param {boolean} props.isDtD
 * @param {string} props.matchingEmployerLabel
 * @param {string} props.matchingEmployerHelpText
 * @param {boolean} props.noLeftGutter
 * @param {string} props.path
 * @param {string} props.providerLogoSrc .
 * @param {string} props.providerLabel
 * @param {boolean} props.searchEnabled
 * @param {string} props.teaserLabel
 * @param {Function} props.updateReduxValues
 * @param {boolean} props.isVisible
 * @param {boolean} props.displayTeaser
 * @returns {React.JSX.Element}
 */
const AutomatedEmployerMatching = ({
    spinnerColor,
    employerField = "",
    sectionHeading,
    description,
    isDtD,
    matchingEmployerLabel,
    matchingEmployerHelpText = "",
    noLeftGutter = false,
    path,
    providerLogoSrc,
    providerLabel,
    searchEnabled = false,
    teaserLabel,
    updateReduxValues = () => {},
    isVisible = false,
    displayTeaser = false,
}) => {
    const { values, setValues } = useFormikContext();
    const { Matching_Company_Name } = values;

    const [searchResults, setSearchResults] = useState([]);
    const [searchActive, setSearchActive] = useState(false);
    const [searching, setSearching] = useState(false);
    const [selection, setSelection] = useState({});
    const [matchConfirmed, setMatchConfirmed] = useState(
        !!Matching_Company_Name,
    );
    const [focusInput, setFocusInput] = useState(false);

    const handleCancelButton = (e) => {
        if (e.type === "keydown" && e.key !== "Enter") return;
        e.preventDefault();
        setSearching(false);
        setSelection({});
        setSearchResults([]);
        setMatchConfirmed(false);
        setFocusInput(true);
        // clear selection values
        setValues({
            ...values,
            Employer: "",
            Matching_Company_Name: "",
            Matching_Company_ID: "",
            Matching_Parent_Raw_ID: "",
            Matching_Contact_Address_Line_1: "",
            Matching_Contact_Address_Line_2: "",
            Matching_Contact_City: "",
            Matching_Contact_State: "",
            Matching_Contact_Zip: "",
            Matching_Contact_Phone: "",
            Matching_Contact_Email: "",
            Matching_Donor_Found: "",
            Matching_Donor_Verified: false,
        });
    };

    const headerIconGlyph = !matchConfirmed
        ? "gifts-duotone"
        : "check-circle-solid";

    const searchOrConfirmationHeading = matchConfirmed
        ? `${values.Matching_Company_Name} is likely to match your gift!`
        : sectionHeading;

    const searchOrConfirmationDescription = matchConfirmed
        ? "Next steps will be provided via email after you've submitted your gift."
        : description;

    const shouldColorIcon =
        searchActive || matchConfirmed || searchResults?.length > 0;

    const employerLookupProps = {
        setSearching,
        selection,
        setSelection,
        path,
        employerField,
        isDtD,
        searchEnabled,
        searching,
        searchResults,
        setSearchResults,
        matchingEmployerLabel,
        matchingEmployerHelpText,
        setMatchConfirmed,
        setSearchActive,
        updateReduxValues,
        spinnerColor,
        handleCancelButton,
        focusInput,
        setFocusInput,
    };

    return (
        <>
            {isVisible && (
                <div
                    className={cx(
                        "col col--12",
                        noLeftGutter && "-padding-left--0",
                    )}
                >
                    <section>
                        <div className="-callout -padding--30">
                            <div className="gift-matching qg-vendor-gift-matching">
                                <EmployerMatchingHeader
                                    sectionHeading={searchOrConfirmationHeading}
                                    description={
                                        searchOrConfirmationDescription
                                    }
                                    iconGlyph={headerIconGlyph}
                                    handleCancelButton={handleCancelButton}
                                    shouldColorIcon={shouldColorIcon}
                                    matchConfirmed={matchConfirmed}
                                />
                                {!matchConfirmed && (
                                    <EmployerLookup {...employerLookupProps} />
                                )}
                                {matchConfirmed && (
                                    <EmployerMatchingFooter
                                        providerLogoSrc={providerLogoSrc}
                                        providerLabel={providerLabel}
                                    />
                                )}
                            </div>
                        </div>
                    </section>
                </div>
            )}
            {displayTeaser && (
                <AutomatedEmployerMatchingTeaser
                    noLeftGutter={noLeftGutter}
                    teaserLabel={teaserLabel}
                />
            )}
        </>
    );
};

export default AutomatedEmployerMatching;
